/**
 * LandingHero — the homepage `/` design.
 *
 * Verbatim render of `home.html` (CSS / HTML / JS are the three string
 * modules in ./landing/landing-*.js). Uses the shared `PageHost`
 * wrapper for CSS scoping, font loading, script injection, link
 * interception, the global `.reveal` collision fix and the topbar hide.
 *
 * Homepage-specific behaviour:
 *   - `wireLinks={true}` + a `LINK_MAP`: the homepage ships placeholder
 *     anchor hrefs (`#contact`, `#ledger`, …). PageHost rewrites these
 *     to real app routes on mount (so right-click / middle-click /
 *     browser hover-tooltip all use the real URL) and intercepts plain
 *     clicks for SPA navigation. `#investors` falls back to `/contact`
 *     until the dedicated investor hub exists.
 *   - Tablet WhoFor cascade fix in `extraPatches` (the file's CSS has a
 *     specificity gap at 761-820px that collapses the active panel).
 *   - Monotonic scroll-driven HIW asset-card build via `onMount`. Each
 *     step's top crossing the upper 40% of the viewport advances
 *     `maxReached`. Hover behaviour is preserved; mouseleave returns to
 *     the highest reached step, not the file's `assembled()` default.
 *     Scrolling back up never reverses the state.
 */
import { STYLES } from "./landing/landing-styles";
import { MARKUP } from "./landing/landing-markup";
import { SCRIPT } from "./landing/landing-script";
import { PageHost } from "./landing/PageHost";

const HOST_CLASS = "trex-hero-host";

// Confirmed mapping (user-approved).
//   `#investors` falls back to `/contact` until the dedicated investor hub
//   page exists — flip this to `/investors` (or wherever) when ready.
//   `#announcement` is hard-coded to the Apex article for now; can be made
//   dynamic later (latest featured CMS article).
const LINK_MAP = {
  "#contact":      "/contact",
  "#how":          "/network",
  "#standard":     "/network/erc-3643",
  "#appstore":     "/solutions/app-store",
  "#compliance":   "https://docs.t-rex.network/",
  "#ledger":       "/network/ledger",
  "#institutions": "/solutions/institutions",
  "#builders":     "/solutions/builders",
  "#investors":    "/contact",
  "#announcement": "/articles/apex-group-to-enable-global-distribution-of-tokenized-funds-using-the-t-rex-ledger-as-its-multi-chain-orchestration-layer",
};

const HOMEPAGE_EXTRA_PATCHES = `
/* TABLET RESPONSIVE PATCH (761-820px).
   The file's @media(max-width:820px) rule converts the WhoFor panels
   from a horizontal accordion to a vertical stack and sets
   .panel-content { position:static; width:auto } — but only via
   ".panel-content, .panel:not(.active) .panel-content". The base
   .panel-content rule (defined LATER in source order with the same
   specificity) re-applies position:absolute; width:clamp(220px,...)
   to the ACTIVE panel, collapsing its body to 0px on tablet. */
@media (max-width: 820px) {
  .${HOST_CLASS} .panel.active .panel-content,
  .${HOST_CLASS} .panel-content {
    position: static !important;
    inset: auto !important;
    width: auto !important;
    max-width: none !important;
  }
}
`;


function wireMonotonicHIW(root) {
  const sec = root.querySelector("#how");
  if (!sec) return undefined;

  const steps = Array.from(sec.querySelectorAll(".step"));
  const layers = Array.from(sec.querySelectorAll(".ac-layer"));
  const pds = Array.from(sec.querySelectorAll(".ac-progress .pd"));
  const badge = sec.querySelector("#acBadge");
  const stepName = sec.querySelector("#acStepName");
  const names = { 1: "Issue", 2: "Distribute", 3: "Manage" };
  const badges = { 1: "On T-REX Ledger", 2: "Distributed", 3: "In service" };

  let maxReached = 1;
  let hovering = false;
  const detach = [];

  function applyStep(n) {
    steps.forEach((s) =>
      s.classList.toggle("active", +s.dataset.step === n)
    );
    layers.forEach((l) =>
      l.classList.toggle("on", +l.dataset.layer <= n)
    );
    pds.forEach((p, i) => {
      const idx = i + 1;
      p.classList.toggle("done", idx < n);
      p.classList.toggle("cur", idx === n);
    });
    if (n >= 3) {
      steps.forEach((s) => s.classList.add("active"));
      pds.forEach((p) => {
        p.classList.add("done");
        p.classList.remove("cur");
      });
    }
    if (badge) {
      badge.textContent = badges[n] || badges[3];
      badge.classList.toggle("svc", n >= 3);
    }
    if (stepName) stepName.textContent = names[n] || names[3];
  }
  const rest = () => applyStep(maxReached);

  // Card starts on step 1 and unfolds as the reader scrolls past each step
  // (monotonic: never walks back), so the build is seen without hovering.
  requestAnimationFrame(() => applyStep(1));

  let raf = 0;
  const onScroll = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      const vh = window.innerHeight;
      const triggerLine = vh * 0.4;
      let highest = maxReached;
      steps.forEach((s) => {
        const r = s.getBoundingClientRect();
        if (r.top < triggerLine) {
          const n = +s.dataset.step;
          if (n > highest) highest = n;
        }
      });
      if (highest > maxReached) {
        maxReached = highest;
        if (!hovering) rest();
      }
    });
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  detach.push(() => window.removeEventListener("scroll", onScroll));
  onScroll();

  steps.forEach((s) => {
    const onEnter = () => {
      hovering = true;
    };
    const onFocus = () => {
      hovering = true;
    };
    s.addEventListener("mouseenter", onEnter);
    s.addEventListener("focusin", onFocus);
    detach.push(() => s.removeEventListener("mouseenter", onEnter));
    detach.push(() => s.removeEventListener("focusin", onFocus));
  });
  const stepsWrap = sec.querySelector("#hiwSteps");
  if (stepsWrap) {
    const onLeave = () => {
      // hold the last hovered step; the card no longer unfolds on mouse-leave
      hovering = false;
    };
    stepsWrap.addEventListener("mouseleave", onLeave);
    detach.push(() => stepsWrap.removeEventListener("mouseleave", onLeave));
  }

  return () => detach.forEach((fn) => fn());
}

export function LandingHero() {
  return (
    <PageHost
      styles={STYLES}
      markup={MARKUP}
      script={SCRIPT}
      hostClass={HOST_CLASS}
      wireLinks={true}
      linkMap={LINK_MAP}
      extraPatches={HOMEPAGE_EXTRA_PATCHES}
      onMount={wireMonotonicHIW}
      data-testid="landing-hero"
    />
  );
}
